/**
 * Drill Register Hook - Auto-populated with required drills + user additions
 * Following useEquipmentRegister pattern exactly
 * Uses Domain Requirements as single source of truth
 */

/**
 * PHASE 3.6 MIGRATION NOTES:
 *
 * This hook uses PARALLEL WRITE pattern for IndexedDB migration:
 * 1. All existing localStorage writes remain unchanged
 * 2. Added parallel storageRouter.writeJSON() calls (fire-and-forget)
 * 3. localStorage is primary (synchronous, reliable)
 * 4. IndexedDB is secondary (asynchronous, tested in background)
 *
 * Why this pattern:
 * - React setState updaters cannot be async (line 395)
 * - Drill register is critical data path (drill management, compliance tracking)
 * - Zero downtime requirement for maritime operations
 * - Gradual migration with safety net (can rollback instantly)
 *
 * Migration points:
 * - Line 395: Main drill register data write (inside setState updater)
 * - Line 706: Backward compatibility sync to drill_settings
 *
 * Metadata NOT migrated (localStorage-only):
 * - Lines 491, 493: Auto-populate flags (ephemeral, per-session)
 * - Lines 600: Migration completion flag (ephemeral)
 * - Lines 647, 659: Vessel config fingerprints (change detection only)
 *
 * Next phases:
 * - Phase 3.7: Verify both storages match for 2+ weeks
 * - Phase 3.8: Switch reads to IndexedDB (localStorage backup)
 * - Phase 3.9: Remove localStorage writes (IndexedDB primary)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useStorageState } from '../useStorageState';
import { devLog } from '../../utils/devLog';
import { useVessel } from '../../contexts/VesselContext';
import { mapDrillsToRegister, getDrillRegisterStats, DRILL_TYPES, COMPLIANCE_LEVELS } from '../../services/drills/drillRegisterMapper';
import { readJson, writeJson } from '../../utils/storageKey';
import * as storageRouter from '../../lib/storageRouter';
import { toOrgScopedKey } from '../../lib/storage';  // FT-66: For fresh localStorage read

// Schema version for migrations - bump when mapper/system fields change
const REGISTER_SCHEMA_VERSION = 1;
const MAPPER_VERSION = 1;

// Storage keys
const LS_KEYS = {
  items: 'drill_register',
  meta: 'drill_register_meta'
};

// Normalize vessel ID to handle quoted strings
const normalizeVesselId = (id) => {
  if (id == null) return '';
  const s = String(id);
  try { return JSON.parse(s); } catch { return s.replace(/^"|"$/g, ''); }
};

// ============================================================================
// FINGERPRINT UTILITIES FOR VESSEL CONFIG CHANGE DETECTION
// ============================================================================

/**
 * Extract only the vessel config fields that affect drill requirements
 * This ensures we only trigger sync when relevant fields change
 */
function pickForDrillRequirements(vessel) {
  // Include ONLY fields that the drill requirements mapper actually uses
  const {
    // Core vessel properties
    category = null,
    type = null,
    vesselType = null,
    lengthOverall = 0,
    
    // Crew and passengers
    crewCount = 0,
    crew_count = 0,
    passengerCapacity = 0,
    passenger_count = 0,
    
    // Safety equipment that affects drills
    SOLAS_applicable = false,
    GMDSS_equipped = false,
    SOPEP_applicable = false,
    oil_capacity_litres = 0,
    oilCapacity = 0,
    medical_stores_category = null,
    medicalCategory = null,
    
    // Operational features
    enclosed_spaces_present = false,
    hasEnclosedSpaces = false,
    towing_capable = false,
    emergency_steering = false,
    hasEmergencySteering = false,
    power_critical_systems = false,
    emergencyGenerator = false,
    
    // Company overrides
    companyRequiredDrills = [],
    companyCustomDrills = [],
    
    // Operating status (inland vs sea-going)
    operatingStatus = null
  } = vessel || {};
  
  const obj = {
    category,
    type: type || vesselType,
    lengthOverall,
    crewCount: crewCount || crew_count,
    passengerCapacity: passengerCapacity || passenger_count,
    SOLAS_applicable,
    GMDSS_equipped,
    SOPEP_applicable,
    oil_capacity_litres: oil_capacity_litres || oilCapacity,
    medical_stores_category: medical_stores_category || medicalCategory,
    enclosed_spaces_present: enclosed_spaces_present || hasEnclosedSpaces,
    towing_capable,
    emergency_steering: emergency_steering || hasEmergencySteering || SOLAS_applicable,
    power_critical_systems,
    emergencyGenerator,
    operatingStatus,
    companyRequiredDrills,
    companyCustomDrills
  };
  
  // Canonicalize: sort keys, remove undefined values
  return Object.keys(obj).sort().reduce((o, k) => {
    const val = obj[k];
    if (val !== undefined) o[k] = val;
    return o;
  }, {});
}

/**
 * Generate a stable fingerprint of vessel config that affects drill requirements
 * FT-112: Now includes drills_config (companyRequiredDrills, drillFrequencies)
 */
function generateFingerprint(vessel, drillsConfig = null) {
  const core = pickForDrillRequirements(vessel);

  // FT-112: Include drills_config fields that affect drill requirements
  const drillsConfigFields = {
    companyRequiredDrills: drillsConfig?.companyRequiredDrills || [],
    drillFrequencies: drillsConfig?.drillFrequencies || {}
  };

  const combined = { ...core, ...drillsConfigFields };
  const canonical = JSON.stringify(combined);

  // Simple hash function
  let h = 0;
  for (let i = 0; i < canonical.length; i++) {
    h = (h * 31 + canonical.charCodeAt(i)) | 0;
  }

  // Include mapper version to force resync when logic changes
  return `${MAPPER_VERSION}:${h}`;
}

// Legacy storage helper for direct access
const legacyGet = (vesselId, key) => {
  const storageKey = `${key}_${normalizeVesselId(vesselId)}`;
  return readJson(storageKey, null);
};

const legacySet = (vesselId, key, value) => {
  const storageKey = `${key}_${normalizeVesselId(vesselId)}`;
  writeJson(storageKey, value);
};

// Migrate from quoted vessel ID storage key to normalized one
function migrateStorageKeyIfNeeded(vesselId) {
  const id = normalizeVesselId(vesselId);
  const goodKey = `drill_register_${id}`;
  const badKey = `drill_register_"${id}"`;

  const bad = readJson(badKey, []);
  const good = readJson(goodKey, []);

  if (bad.length && !good.length) {
    writeJson(goodKey, bad);
    localStorage.removeItem(badKey);

  }
}

// Migration function for existing rows
function migrateDrillRowsIfNeeded(items) {
  if (!items || !Array.isArray(items)) return { changed: false, items: items || [] };
  
  const needsMigration = (row) =>
    !row._schemaVersion || row._schemaVersion < REGISTER_SCHEMA_VERSION;

  if (!items.some(needsMigration)) return { changed: false, items };

  const fix = (row) => {
    if (!needsMigration(row)) return row;
    
    return {
      ...row,
      _schemaVersion: REGISTER_SCHEMA_VERSION
    };
  };

  const migrated = items.map(fix);
  return { changed: true, items: migrated };
}

// Merge user fields from one item to another
const mergeUserFields = (base, user) => ({
  ...base,
  lastCompleted: user.lastCompleted ?? base.lastCompleted,
  lastCompletedBy: user.lastCompletedBy ?? base.lastCompletedBy,
  nextDue: user.nextDue ?? base.nextDue,
  status: user.status ?? base.status,
  notes: user.notes ?? base.notes,
  customFields: { ...(base.customFields || {}), ...(user.customFields || {}) },
  updatedAt: user.updatedAt ?? base.updatedAt
});

export function useDrillRegister() {
  const { currentVessel, currentVesselId } = useVessel();

  // Pass base key only - useStorageState will add vessel suffix
  const { value: drillItems, setValue: setDrillItems, loaded } = useStorageState('drill_register', []);
  
  // Log what we got from storage
  useEffect(() => {
    if (loaded && drillItems) {

    }
  }, [loaded, drillItems?.length]);
  
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [hasMigrated, setHasMigrated] = useState(false);
  
  // Check if we've ever auto-populated for this vessel (persisted)
  // Use normalized vessel ID to avoid issues with quoted strings
  const normalizedVesselId = normalizeVesselId(currentVesselId);
  const hasAutoPopulatedKey = `drill_auto_populated_${normalizedVesselId}`;
  const [hasAutoPopulated, setHasAutoPopulated] = useState(() => {
    // Check both normalized and original keys for backward compatibility
    return localStorage.getItem(hasAutoPopulatedKey) === 'true' ||
           localStorage.getItem(`drill_auto_populated_${currentVesselId}`) === 'true';
  });

  /**
   * Idempotent upsert - merges derived rows with existing, preserving user fields
   * System fields (overwrite): drillType, name, frequencyDays, complianceLevel, regulatorySource, linkedEquipmentIds
   * User fields (preserve): lastCompleted, nextDue, notes, customFields
   */
  const upsertById = useCallback((existing, incoming) => {
    const map = new Map(existing.map(item => [item.id, item]));
    const created = [];
    const updated = [];
    const unchanged = [];

    // Process each incoming row
    for (const row of incoming) {
      const prev = map.get(row.id);
      
      if (!prev) {
        // New drill
        created.push(row);
        map.set(row.id, row);
        continue;
      }

      // Merge: system fields from new, user fields from prev
      const merged = {
        ...row, // System fields (drillType, frequency, etc.)
        // Preserve all user fields
        lastCompleted: prev.lastCompleted,
        lastCompletedBy: prev.lastCompletedBy,
        nextDue: prev.nextDue,
        status: prev.status,
        notes: prev.notes,
        customFields: prev.customFields || {},
        linkedJobIds: prev.linkedJobIds || [],
        // Keep creation time and source
        createdAt: prev.createdAt,
        updatedAt: prev.updatedAt || new Date().toISOString(),
        _schemaVersion: REGISTER_SCHEMA_VERSION
      };

      // Check if actually changed
      const systemFieldsChanged = 
        prev.drillType !== row.drillType ||
        prev.frequencyDays !== row.frequencyDays ||
        prev.complianceLevel !== row.complianceLevel ||
        JSON.stringify(prev.linkedEquipmentIds) !== JSON.stringify(row.linkedEquipmentIds);

      if (systemFieldsChanged) {
        updated.push(merged);
        map.set(row.id, merged);
      } else {
        unchanged.push(prev);
      }
    }

    // Archive orphaned drills that are no longer required
    const incomingIds = new Set(incoming.map(r => r.id));
    const archived = [];
    
    for (const [id, item] of map.entries()) {
      if (!incomingIds.has(id) && 
          !item.archived && 
          !item.custom) { // Don't archive custom drills
        
        const archivedItem = {
          ...item,
          archived: true,
          archivedAt: new Date().toISOString(),
          archivedReason: 'No longer required by current vessel configuration'
        };
        archived.push(archivedItem);
        map.set(id, archivedItem);
      }
    }

    return {
      next: Array.from(map.values()),
      created,
      updated,
      archived,
      unchanged
    };
  }, []);

  /**
   * Get drill register - returns current register
   */
  const getDrillRegister = useCallback(() => {
    const nonArchived = drillItems.filter(item => !item.archived);
    
    // Safety filter: Only include items that belong to current vessel
    const vesselFiltered = nonArchived.filter(item => {
      if (!item.id || !currentVesselId) return true;

      const prefix = `drill:${currentVesselId}:`;
      const belongsToCurrentVessel = item.id.startsWith(prefix);

      if (
        process.env.NODE_ENV === 'development' &&
        item.id.startsWith('drill:') &&
        !belongsToCurrentVessel
      ) {

      }

      return belongsToCurrentVessel;
    });
    
    return vesselFiltered;
  }, [drillItems, currentVesselId]);

  /**
   * Sync from requirements - regenerates from config
   */
  const syncFromRequirements = useCallback(async (vesselConfig = null, equipmentRegister = null, dryRun = false) => {
    const baseConfig = vesselConfig || currentVessel;
    const vesselId = baseConfig?.id || currentVesselId;

    if (!baseConfig || !vesselId) {
      return { success: false, error: 'No vessel selected' };
    }

    setSyncing(true);
    setError(null);

    try {
      // Read company required drills from drills_config (same source as Drill Settings)
      const drillsConfigKey = toOrgScopedKey(`drills_config_${vesselId}`);
      const drillsConfigRaw = localStorage.getItem(drillsConfigKey);
      const drillsConfig = drillsConfigRaw ? JSON.parse(drillsConfigRaw) : {};

      // Merge companyRequiredDrills from drills_config into vessel config
      const config = {
        ...baseConfig,
        companyRequiredDrills: drillsConfig.companyRequiredDrills || [],
        drillFrequencies: drillsConfig.drillFrequencies || {}
      };

      devLog('[useDrillRegister] Merged drills config', {
        companyRequiredDrills: config.companyRequiredDrills,
        drillFrequencies: Object.keys(config.drillFrequencies || {})
      });

      // FT-66 FIX (2026-03-25): Read fresh data from localStorage instead of stale closure
      // The drillItems in closure may contain previous vessel's data due to race condition
      // between async re-hydration and sync syncFromRequirements
      const freshStorageKey = toOrgScopedKey(`drill_register_${vesselId}`);
      const freshRawData = localStorage.getItem(freshStorageKey);
      const freshDrillItems = freshRawData ? JSON.parse(freshRawData) : [];

      // Get existing drill records for last completed/next due
      const drillRecords = {};
      freshDrillItems.forEach(item => {
        // Additional safety: verify item belongs to this vessel by checking ID pattern
        if (item.drillType && (!item.id || item.id.includes(vesselId))) {
          drillRecords[item.drillType] = {
            lastCompleted: item.lastCompleted,
            nextDue: item.nextDue
          };
        }
      });

      // Generate drill register from requirements
      const derived = mapDrillsToRegister(config, vesselId, drillRecords, equipmentRegister);

      devLog('[useDrillRegister] Derived drills:', {
        count: derived.length,
        types: [...new Set(derived.map(r => r.drillType))],
        complianceLevels: [...new Set(derived.map(r => r.complianceLevel))]
      });

      // Use function updater pattern to avoid circular dependency
      const result = await new Promise((resolve) => {
        setDrillItems(existingItems => {
          const existing = existingItems || [];

          // Perform idempotent upsert
          let { next, created, updated, archived, unchanged } = upsertById(existing, derived);

          if (dryRun) {
            devLog('[useDrillRegister] Dry run results:', {
              created: created.length,
              updated: updated.length,
              archived: archived.length,
              unchanged: unchanged.length
            });
            resolve({ 
              success: true, 
              applicableCount: derived.length,
              created, 
              updated, 
              archived, 
              unchanged 
            });
            return existingItems; // Return unchanged for dry run
          }

          // Apply changes if not dry run
          const changeCount = created.length + updated.length + archived.length;
          if (changeCount > 0) {
            // ONE ATOMIC WRITE

            setLastSyncAt(new Date().toISOString());

            // FT-64 FIX (2026-03-24): Verify vessel ID hasn't changed before writing
            // Read ACTUAL current vessel from localStorage (not from React state, which can be stale)
            // This prevents cross-vessel contamination when user switches vessels mid-operation
            const actualCurrentVesselId = localStorage.getItem('currentVesselId');
            const storageKey = `drill_register_${vesselId}`;

            // Extract vesselId from storageKey and compare with actual current vessel
            if (actualCurrentVesselId && actualCurrentVesselId !== vesselId) {
              console.warn(`[FT-64] BLOCKED drill_register write - vessel changed from ${vesselId} to ${actualCurrentVesselId}`);
              devLog('[useDrillRegister] Aborting write due to vessel change');
              resolve({ success: false, error: 'Vessel changed during sync - aborting to prevent data contamination', changeCount: 0 });
              return existingItems; // Return unchanged, don't write stale data
            }

            // Save to localStorage directly as well
            localStorage.setItem(storageKey, JSON.stringify(next));

            // PHASE 3.6: Also write to IndexedDB in parallel (fire-and-forget)
            storageRouter.writeJSON(storageKey, next).catch(error => {
              console.warn('[useDrillRegister] Parallel IndexedDB write failed:', error);
              // localStorage succeeded, so operation is still successful
            });

            devLog('[useDrillRegister] Sync completed:', {
              created: created.length,
              updated: updated.length,
              archived: archived.length,
              total: next.length
            });
            
            resolve({ success: true, created, updated, archived, unchanged, changeCount });
            return next; // Return new state
          } else {

            devLog('[useDrillRegister] No changes needed');
            resolve({ success: true, created: [], updated: [], archived: [], unchanged, changeCount: 0 });
            return existingItems; // Return unchanged
          }
        });
      });

      return result;

    } catch (err) {

      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSyncing(false);
    }
  }, [currentVesselId, upsertById, drillItems, setDrillItems]);

  // Migration and auto-populate on first load
  useEffect(() => {
    // Don't do anything until storage is loaded
    if (!loaded) {

      return;
    }
    
    if (currentVesselId && currentVessel) {
      // Migrate storage key if needed (quoted vessel ID fix)
      migrateStorageKeyIfNeeded(currentVesselId);
      
      // Check if we need schema migration
      if (!hasMigrated) {
        const meta = legacyGet(currentVesselId, LS_KEYS.meta) || { schemaVersion: 0 };
        
        if (meta.schemaVersion < REGISTER_SCHEMA_VERSION) {
          // FT-471: syncFromRequirements() removed from this migration branch — it was
          // regenerating the entire register from vessel requirements, which wrote ghost drills.
          // Row-level field upgrades are handled by migrateDrillRowsIfNeeded() below.
          // Just bump the schema version so this branch doesn't re-run on next mount.
          legacySet(currentVesselId, LS_KEYS.meta, {
            schemaVersion: REGISTER_SCHEMA_VERSION,
            lastMigrationAt: new Date().toISOString()
          });
        }
        
        // Also run legacy migration for any old format items
        if (drillItems && drillItems.length > 0) {
          const { changed, items } = migrateDrillRowsIfNeeded(drillItems);
          if (changed) {
            setDrillItems(items);

          }
        }
        
        setHasMigrated(true);
      }
      
      // Then check if we need to auto-populate
      const hasDataInStorage = drillItems && drillItems.length > 0;
      
      // Check for the REAL data in localStorage to be extra safe
      // Use normalized ID to avoid quoted string issues
      const storageKey = `drill_register_${normalizedVesselId}`;
      const actualStorageData = localStorage.getItem(storageKey);
      const hasActualData = actualStorageData && actualStorageData !== '[]' && actualStorageData !== 'null';
      
      // Also check the non-normalized key for backward compatibility
      const legacyStorageData = localStorage.getItem(`drill_register_${currentVesselId}`);
      const hasLegacyData = legacyStorageData && legacyStorageData !== '[]' && legacyStorageData !== 'null';
      
      // FT-471: syncFromRequirements() is fully disabled from all automatic paths:
      // (1) shouldAutoPopulate = false prevents it firing on first load, and
      // (2) the migration branch above no longer calls it on schema upgrade.
      // It remains callable by explicit user actions (e.g. manual sync button) only.
      const shouldAutoPopulate = false; // was: !hasAutoPopulated && !hasActualData && !hasLegacyData && !hasDataInStorage
      
      if (shouldAutoPopulate) {

        // Use a promise to prevent race conditions
        const autoPopulatePromise = syncFromRequirements().then(result => {
          if (result && result.success) {
            setHasAutoPopulated(true);
            localStorage.setItem(hasAutoPopulatedKey, 'true');
            // Also set the non-normalized key for backward compatibility
            localStorage.setItem(`drill_auto_populated_${currentVesselId}`, 'true');
            
            if (result.created && result.created.length > 0) {

            }
            return result;
          } else {

            return null;
          }
        }).catch(error => {

          return null;
        });
        
        // Store promise to prevent multiple calls
        window.__drillAutoPopulatePromise = autoPopulatePromise;
      } else if (window.__drillAutoPopulatePromise) {
        // Wait for existing auto-populate to complete
        window.__drillAutoPopulatePromise.then(() => {
          delete window.__drillAutoPopulatePromise;
        });
      }
      
      // MIGRATION: Apply existing drill_settings customizations to the new register
      const drillSettingsKey = `drill_settings_${currentVesselId}`;
      const existingSettings = localStorage.getItem(drillSettingsKey);
      
      if (existingSettings && !localStorage.getItem(`drill_settings_migrated_${currentVesselId}`)) {

        try {
          const settings = JSON.parse(existingSettings);
          const vesselSettings = settings[currentVesselId];
          
          if (vesselSettings) {
            const { frequencies, enabled, customDrills } = vesselSettings;
            let hasCustomizations = false;
            
            // Apply custom frequencies and enabled states
            setDrillItems(currentItems => {
              const updated = currentItems.map(item => {
                let modified = false;
                const updatedItem = { ...item };
                
                // Apply custom frequency if exists
                if (frequencies && frequencies[item.drillType] !== undefined) {
                  updatedItem.frequencyDays = frequencies[item.drillType];
                  updatedItem.customFrequency = frequencies[item.drillType];
                  modified = true;
                }
                
                // Apply enabled state if exists
                if (enabled && enabled[item.drillType] !== undefined) {
                  // If disabled, archive the drill
                  if (!enabled[item.drillType]) {
                    updatedItem.archived = true;
                    updatedItem.archivedAt = new Date().toISOString();
                    updatedItem.archivedReason = 'Disabled by user in drill settings';
                  }
                  modified = true;
                }
                
                if (modified) {
                  hasCustomizations = true;
                  updatedItem.updatedAt = new Date().toISOString();
                }
                
                return updatedItem;
              });
              
              // Add custom drills from settings
              if (customDrills && customDrills.length > 0) {
                customDrills.forEach(drill => {
                  const customDrill = {
                    id: `drill:${currentVesselId}:${drill.id}`,
                    drillType: drill.id,
                    name: drill.name,
                    frequencyDays: drill.frequency_days,
                    complianceLevel: COMPLIANCE_LEVELS.REQUIRED,
                    regulatorySource: 'Company SMS Policy',
                    note: drill.description || 'Custom company drill',
                    linkedEquipmentIds: [],
                    linkedEquipmentCategories: [],
                    lastCompleted: null,
                    nextDue: null,
                    configurable: true,
                    custom: true,
                    archived: !drill.enabled,
                    archivedAt: !drill.enabled ? new Date().toISOString() : null,
                    archivedReason: !drill.enabled ? 'Disabled in drill settings' : null,
                    createdAt: vesselSettings.lastModified || new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  };
                  
                  updated.push(customDrill);
                  hasCustomizations = true;
                });
              }
              
              if (hasCustomizations) {

              }
              
              return updated;
            });
            
            // Mark as migrated
            localStorage.setItem(`drill_settings_migrated_${currentVesselId}`, 'true');

          }
        } catch (err) {

        }
      }
    }
  }, [currentVesselId, syncFromRequirements, loaded, drillItems, setDrillItems, currentVessel, hasMigrated, hasAutoPopulated]);

  // Track when we've just loaded to prevent immediate sync
  const justLoadedFromStorageRef = useRef(true);
  
  // Fingerprint-based vessel config change detection
  // FT-112: Also monitors drills_config for company required drills changes
  useEffect(() => {
    // Don't sync until storage is loaded
    if (!loaded) {

      return;
    }

    // Don't sync on initial mount or if not ready
    if (!currentVesselId || !currentVessel || !hasAutoPopulated || !hasMigrated) {

      return;
    }

    // FT-112: Read drills_config for fingerprint generation
    const drillsConfigKey = toOrgScopedKey(`drills_config_${currentVesselId}`);
    const drillsConfigRaw = localStorage.getItem(drillsConfigKey);
    const drillsConfig = drillsConfigRaw ? JSON.parse(drillsConfigRaw) : {};

    // Generate fingerprint of current vessel config that affects drill requirements
    const fpKey = `drill_req_fp_${currentVesselId}`;
    const nextFp = generateFingerprint(currentVessel, drillsConfig);
    const prevFp = localStorage.getItem(fpKey);

    // Skip if fingerprint unchanged (no relevant config changes)
    if (nextFp === prevFp) {
      return;
    }

    // EXTRA SAFETY: Don't sync within 2 seconds of page load to avoid race conditions
    if (justLoadedFromStorageRef.current) {

      setTimeout(() => {
        justLoadedFromStorageRef.current = false;
        // Recheck after timeout in case config actually changed
        // FT-112: Re-read drills_config for fingerprint
        const freshDrillsConfigRaw = localStorage.getItem(drillsConfigKey);
        const freshDrillsConfig = freshDrillsConfigRaw ? JSON.parse(freshDrillsConfigRaw) : {};
        const currentFp = generateFingerprint(currentVessel, freshDrillsConfig);
        if (currentFp !== localStorage.getItem(fpKey)) {

          syncFromRequirements();
          localStorage.setItem(fpKey, currentFp);
        }
      }, 2000);
      return;
    }

    // Config actually changed - now sync

    // Perform sync
    syncFromRequirements();

    // Update fingerprint AFTER successful sync
    localStorage.setItem(fpKey, nextFp);
  }, [currentVesselId, syncFromRequirements, loaded, currentVessel, hasAutoPopulated, hasMigrated]);

  // FT-112 REWRITE: Event listener removed - no longer needed
  // SafetyDrillsTab now reads directly from useDrillsConfig which uses useStorageState
  // React handles reactivity automatically when drills_config changes

  /**
   * Sync changes back to drill_settings for backward compatibility
   */
  const syncToDrillSettings = useCallback(() => {
    if (!currentVesselId) return;
    
    const drillSettingsKey = `drill_settings_${currentVesselId}`;
    const existingSettings = localStorage.getItem(drillSettingsKey);
    
    // Build settings from current register
    const frequencies = {};
    const enabled = {};
    const customDrills = [];
    
    drillItems.forEach(item => {
      if (item.custom) {
        customDrills.push({
          id: item.drillType,
          name: item.name,
          frequency_days: item.frequencyDays,
          description: item.note,
          enabled: !item.archived
        });
      } else {
        // Only save if different from default
        if (item.customFrequency !== undefined || item.frequencyDays !== item.originalFrequency) {
          frequencies[item.drillType] = item.frequencyDays;
        }
        enabled[item.drillType] = !item.archived;
      }
    });
    
    const vesselSettings = {
      frequencies,
      enabled,
      customDrills,
      lastModified: new Date().toISOString(),
      modifiedBy: 'Drill Register System'
    };
    
    // Merge with existing settings structure
    const allSettings = existingSettings ? JSON.parse(existingSettings) : {};
    allSettings[currentVesselId] = vesselSettings;

    // FT-64 FIX (2026-03-24): Verify vessel ID hasn't changed before writing
    // Read ACTUAL current vessel from localStorage (not from React state, which can be stale)
    // This prevents cross-vessel contamination when user switches vessels mid-operation
    const actualCurrentVesselId = localStorage.getItem('currentVesselId');
    if (actualCurrentVesselId && actualCurrentVesselId !== currentVesselId) {
      console.warn(`[FT-64] BLOCKED drill_settings write - vessel changed from ${currentVesselId} to ${actualCurrentVesselId}`);
      devLog('[useDrillRegister] Aborting drill_settings write due to vessel change');
      return; // Don't write stale data to wrong vessel
    }

    localStorage.setItem(drillSettingsKey, JSON.stringify(allSettings));

    // PHASE 3.6: Also write to IndexedDB in parallel (fire-and-forget)
    storageRouter.writeJSON(drillSettingsKey, allSettings).catch(error => {
      console.warn('[useDrillRegister] Backward compatibility parallel write failed:', error);
      // localStorage succeeded, so operation is still successful
    });

    devLog('[useDrillRegister] Synced to drill_settings for backward compatibility');
  }, [currentVesselId, drillItems]);

  /**
   * Update drill frequency - for customization
   */
  const updateDrillFrequency = useCallback((drillType, newFrequency) => {
    setDrillItems(prev => {
      const updated = prev.map(item => {
        if (item.drillType === drillType) {
          return {
            ...item,
            frequencyDays: newFrequency,
            customFrequency: newFrequency, // Track custom value
            configurable: true,
            updatedAt: new Date().toISOString()
          };
        }
        return item;
      });
      
      // Sync back to drill_settings after update
      setTimeout(() => syncToDrillSettings(), 100);
      
      return updated;
    });
    
    devLog('[useDrillRegister] Drill frequency updated:', drillType, newFrequency);
    return true;
  }, [setDrillItems, syncToDrillSettings]);

  /**
   * Add custom drill - for company-specific drills
   */
  const addCustomDrill = useCallback((drill) => {
    const newDrill = {
      id: `drill:${currentVesselId}:custom_${Date.now()}`,
      drillType: drill.drillType || `custom_${Date.now()}`,
      name: drill.name || 'Custom Drill',
      frequencyDays: drill.frequencyDays || 180,
      complianceLevel: drill.complianceLevel || COMPLIANCE_LEVELS.REQUIRED,
      regulatorySource: 'Company SMS Policy',
      note: drill.note || 'Custom company drill',
      linkedEquipmentIds: drill.linkedEquipmentIds || [],
      linkedEquipmentCategories: drill.linkedEquipmentCategories || [],
      lastCompleted: null,
      nextDue: null,
      configurable: true,
      custom: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setDrillItems(prev => {
      const updated = [...prev, newDrill];
      
      // Sync back to drill_settings after adding
      setTimeout(() => syncToDrillSettings(), 100);
      
      return updated;
    });
    
    devLog('[useDrillRegister] Custom drill added:', newDrill.id);
    return newDrill;
  }, [currentVesselId, setDrillItems, syncToDrillSettings]);

  // Update a drill item (for recording completions, etc.)
  const updateDrill = useCallback((id, updates) => {
    setDrillItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, ...updates, updatedAt: new Date().toISOString() };
      }
      return item;
    }));
    
    devLog('[useDrillRegister] Drill updated:', id);
    return true;
  }, [setDrillItems]);

  // Archive a drill
  const archiveDrill = useCallback((id, reason = 'User archived') => {
    setDrillItems(prev => prev.map(item => 
      item.id === id 
        ? { 
            ...item, 
            archived: true, 
            archivedAt: new Date().toISOString(),
            archivedReason: reason 
          }
        : item
    ));
    devLog('[useDrillRegister] Drill archived:', id);
    return true;
  }, [setDrillItems]);

  // Remove a drill completely
  const removeDrill = useCallback((id) => {
    const item = drillItems.find(i => i.id === id);
    if (!item) return false;

    // Only allow removal of custom drills
    if (!item.custom) {

      return false;
    }

    setDrillItems(prev => prev.filter(i => i.id !== id));
    devLog('[useDrillRegister] Drill removed:', id);
    return true;
  }, [drillItems, setDrillItems]);

  // Get filtered drills
  const getFilteredDrills = useCallback((filter) => {
    const allDrills = getDrillRegister();
    
    switch (filter) {
      case 'required':
        return allDrills.filter(d => d.complianceLevel === COMPLIANCE_LEVELS.REQUIRED);
      case 'recommended':
        return allDrills.filter(d => d.complianceLevel === COMPLIANCE_LEVELS.RECOMMENDED);
      case 'optional':
        return allDrills.filter(d => d.complianceLevel === COMPLIANCE_LEVELS.OPTIONAL);
      case 'custom':
        return allDrills.filter(d => d.custom === true);
      case 'overdue':
        const now = new Date();
        return allDrills.filter(d => d.nextDue && new Date(d.nextDue) < now);
      case 'archived':
        return drillItems.filter(d => d.archived === true);
      case 'all':
      default:
        return allDrills;
    }
  }, [getDrillRegister, drillItems]);

  // Get drill by type
  const getDrillByType = useCallback((drillType) => {
    const allDrills = getDrillRegister();
    return allDrills.find(d => d.drillType === drillType);
  }, [getDrillRegister]);

  // Get statistics
  const getStats = useCallback(() => {
    const allDrills = getDrillRegister();
    return getDrillRegisterStats(allDrills);
  }, [getDrillRegister]);

  return {
    // Primary API
    drillRegister: getDrillRegister(),
    getDrillRegister,
    syncFromRequirements,
    updateDrillFrequency,
    addCustomDrill,
    
    // CRUD operations
    updateDrill,
    archiveDrill,
    removeDrill,
    
    // Filtering and queries
    getFilteredDrills,
    getDrillByType,
    getStats,
    
    // State
    syncing,
    error,
    lastSyncAt,
    loaded,
    
    // Constants
    DRILL_TYPES,
    COMPLIANCE_LEVELS,
    
    // Debug
    debugState: {
      currentVesselId,
      loaded,
      hasAutoPopulated,
      drillItemsCount: drillItems?.length || 0,
      hasMigrated
    }
  };
}